import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import { supplierService, SupplierDTO } from '../../services/supplierService';
import '../../pages/Products/ProductCategoryDetailPage.css';
import { ArrowLeft, PenLine, Trash2, ShoppingBag, FileText } from 'lucide-react';

const SupplierDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [supplier, setSupplier] = useState<SupplierDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSupplier();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadSupplier = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await supplierService.getSupplierById(id || '0');
      if (response?.data?.data) {
        setSupplier(response.data.data);
      }
      setLoading(false);
    } catch {
      setError(t('supplier.failedToLoadSupplier'));
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="detail-loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!supplier) {
    return (
      <Layout>
        <div className="detail-container">
          <div className="alert alert-danger">{error || t('supplier.supplierNotFound')}</div>
          <button className="detail-breadcrumb-btn" onClick={() => navigate('/suppliers')}>
            {t('supplier.backToSuppliers')}
          </button>
        </div>
      </Layout>
    );
  }

  const langCode = t('common.languageCode') === 'ja' ? 'ja-JP' : 'en-US';

  return (
    <Layout>
      <div className="detail-container">
        <nav className="detail-breadcrumb">
          <button
            className="detail-breadcrumb-btn"
            onClick={() => navigate('/suppliers')}
          >
            <ArrowLeft size={16} strokeWidth={2} />
            {t('supplier.backToSuppliers')}
          </button>
        </nav>

        <div className="detail-header">
          <div>
            <h1 className="detail-title">{supplier.name}</h1>
            <div className="detail-meta">
              <span className="detail-meta-item">
                <code>{supplier.code}</code>
              </span>
              <span className="detail-meta-item">
                <span className={`badge ${supplier.isActive ? 'badge-active' : 'badge-inactive'}`}>
                  {supplier.isActive ? t('supplier.active') : t('supplier.inactive')}
                </span>
              </span>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            {error}
            <button type="button" className="btn-close" onClick={() => setError(null)} />
          </div>
        )}

        <div className="detail-content">
          <div className="detail-main-section">

            <div className="detail-card">
              <div className="detail-card-header">
                <h2 className="detail-card-title">{t('supplier.supplierInfo')}</h2>
              </div>
              <div className="detail-card-body">
                <div className="detail-grid">
                  <div className="detail-field">
                    <span className="detail-label">{t('supplier.supplierCode')}</span>
                    <span className="detail-value"><code>{supplier.code}</code></span>
                  </div>
                  <div className="detail-field">
                    <span className="detail-label">{t('supplier.supplierName')}</span>
                    <span className="detail-value">{supplier.name}</span>
                  </div>
                  {supplier.paymentTerms && (
                    <div className="detail-field">
                      <span className="detail-label">{t('supplier.paymentTerms')}</span>
                      <span className="detail-value">
                        {supplier.paymentTerms === 'Cash'
                          ? t('supplier.paymentTermsCash')
                          : supplier.paymentTerms === 'Credit'
                            ? t('supplier.creditPeriodDaysDisplay', { days: supplier.creditPeriodDays || 0 })
                            : supplier.paymentTerms}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="detail-card">
              <div className="detail-card-header">
                <h2 className="detail-card-title">{t('supplier.contactDetails')}</h2>
              </div>
              <div className="detail-card-body">
                <div className="detail-grid">
                  <div className="detail-field">
                    <span className="detail-label">{t('supplier.contactEmail')}</span>
                    <span className="detail-value">{supplier.contactEmail}</span>
                  </div>
                  <div className="detail-field">
                    <span className="detail-label">{t('supplier.contactPhone')}</span>
                    <span className="detail-value">{supplier.contactPhone || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>

            {(supplier.address || supplier.city || supplier.state || supplier.postalCode || supplier.country) && (
              <div className="detail-card">
                <div className="detail-card-header">
                  <h2 className="detail-card-title">{t('supplier.addressDetails')}</h2>
                </div>
                <div className="detail-card-body">
                  <div className="detail-grid">
                    {supplier.address && (
                      <div className="detail-field">
                        <span className="detail-label">{t('supplier.address')}</span>
                        <span className="detail-value">{supplier.address}</span>
                      </div>
                    )}
                    {supplier.city && (
                      <div className="detail-field">
                        <span className="detail-label">{t('supplier.city')}</span>
                        <span className="detail-value">{supplier.city}</span>
                      </div>
                    )}
                    {supplier.state && (
                      <div className="detail-field">
                        <span className="detail-label">{t('supplier.state')}</span>
                        <span className="detail-value">{supplier.state}</span>
                      </div>
                    )}
                    {supplier.postalCode && (
                      <div className="detail-field">
                        <span className="detail-label">{t('supplier.postalCode')}</span>
                        <span className="detail-value">{supplier.postalCode}</span>
                      </div>
                    )}
                    {supplier.country && (
                      <div className="detail-field">
                        <span className="detail-label">{t('supplier.country')}</span>
                        <span className="detail-value">{supplier.country}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="detail-card">
              <div className="detail-card-header">
                <h2 className="detail-card-title">{t('supplier.auditInformation')}</h2>
              </div>
              <div className="detail-card-body">
                <div className="detail-grid">
                  <div className="detail-field">
                    <span className="detail-label">{t('supplier.supplierId')}</span>
                    <span className="detail-value">{supplier.id}</span>
                  </div>
                  <div className="detail-field">
                    <span className="detail-label">{t('supplier.createdDate')}</span>
                    <span className="detail-value">
                      {new Date(supplier.createdAt).toLocaleDateString(langCode)}{' '}
                      {new Date(supplier.createdAt).toLocaleTimeString(langCode)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

          </div>

          <div className="detail-sidebar">
            <div className="detail-card">
              <div className="detail-card-header">
                <h3 className="detail-card-title">{t('common.actions')}</h3>
              </div>
              <div className="detail-card-body">
                <div className="detail-actions">
                  <button
                    className="btn-save"
                    onClick={() => navigate(`/suppliers/${supplier.id}/edit`)}
                  >
                    <PenLine size={16} strokeWidth={2} />
                    {t('supplier.editSupplier')}
                  </button>
                  <button
                    className="btn-delete"
                    onClick={() => navigate(`/suppliers/${supplier.id}/delete`)}
                  >
                    <Trash2 size={16} strokeWidth={2} />
                    {t('common.delete')}
                  </button>
                  <button
                    className="detail-btn detail-btn-secondary"
                    onClick={() => navigate('/suppliers')}
                  >
                    <ArrowLeft size={16} strokeWidth={2} />
                    {t('supplier.backToSuppliers')}
                  </button>
                </div>
              </div>
            </div>

            <div className="detail-card">
              <div className="detail-card-header">
                <h3 className="detail-card-title">{t('supplier.quickActions')}</h3>
              </div>
              <div className="detail-card-body">
                <button
                  className="detail-quick-link"
                  onClick={() => navigate(`/purchase-items?supplierId=${supplier.id}`)}
                >
                  <ShoppingBag size={16} strokeWidth={2} />
                  {t('supplier.viewPurchaseItems')}
                </button>
                <button
                  className="detail-quick-link"
                  onClick={() => navigate('/purchase-orders')}
                >
                  <FileText size={16} strokeWidth={2} />
                  {t('supplier.viewPurchaseOrders')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SupplierDetailPage;
